import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Card, CardContent, CardHeader, CardDescription, Button, Icon, Loader as Spinner, Segment } from 'semantic-ui-react';
import { useGetSimilarServicesQuery } from '@/services/bmApi';
import { Service, SimilarService } from '@/types';
import { useAppDispatch, useAppSelector } from '@/hooks';
import * as actions from '@/store/actions';
import { Flex } from 'grid-styled';
import classNames from 'classnames/bind';
import styles from './SimilarServices.module.css'
import { formatMinutesToWords } from '@/utils/datetime';
import { createSimilarService } from '@/store/reducers/similarServicesSlice';
import useTranslate from '@/hooks/useTranslate';

let cx = classNames.bind(styles);

interface Props {
    serviceIds?: number[];
    onSelect?: (service: SimilarService) => void
    style?: React.CSSProperties
    create?: boolean
    /**
     * load all services and send all ids
     * to the generate services
     */
    loadServices?: boolean
    /**
     * When false, SimilarServices will not fetch any data.
     * Intended for use with the sidebar so we only fetch once opened.
     */
    enabled?: boolean
}

const SimilarServices: React.FC<Props> = ({
    serviceIds = [],
    onSelect = () => { },
    create = false,
    loadServices = false,
    enabled = true,
}) => {
    const dispatch = useAppDispatch();
    const services = useAppSelector(s => s.services);
    const company = useAppSelector((s) => s.company);

    const { translate } = useTranslate();

    const currencySign =
        // try a few likely shapes of the company payload, fallback to 'kr'
        (company.data as any)?.CurrencyInfo?.CurrencySign ??
        (company.data as any)?.CurrencySign ??
        'kr';

    // IMPORTANT: the existing <Loader requirements={...} /> pattern does not reliably
    // re-dispatch when requirements change (e.g. when opening the sidebar). So we
    // explicitly fetch services when the sidebar is opened.
    useEffect(() => {
        if (!enabled) return;
        if (!loadServices) return;
        if (services.isLoaded || services.isLoading) return;

        dispatch(actions.FETCH_SERVICES.request());
    }, [dispatch, enabled, loadServices, services.isLoaded, services.isLoading]);

    const _serviceIds = useMemo(
        () => (loadServices ? services.data?.map((s: Service) => s.Id) : serviceIds),
        [loadServices, services.data, serviceIds]
    );

    const shouldSkipQuery = !enabled || !_serviceIds || _serviceIds.length === 0;

    const similarServicesQuery = useGetSimilarServicesQuery(
        {
            ServiceIds: _serviceIds,
        },
        {
            skip: shouldSkipQuery,
        }
    );

    const similarService = useAppSelector(s => s.similarService);
    const isCreating = create && similarService.isLoading;

    return (
        <div className={cx(['similar-services', isCreating ? 'disable' : ''])}>
            {isCreating ? <Spinner active size='small' /> : null}
            {enabled && loadServices && services.isLoading ? <Spinner active size='small' /> : null}
            {similarServicesQuery.isFetching ? <Spinner active size='small' /> : null}

            {enabled && !similarServicesQuery.isFetching && similarServicesQuery.data?.SimilarServices.length === 0
                ? (<p>{translate('similarServices.noServicesFound')}</p>)
                : null}

            {similarServicesQuery.data?.SimilarServices.map((service, index) => {
                return (
                    <Card
                        key={index}
                        className={cx('service-card')}
                        onClick={() => {
                            onSelect(service);
                            if (create) {
                                dispatch(createSimilarService(service));
                            }
                        }}
                        fluid
                    >
                        <CardContent>
                            <CardHeader>
                                <span className={cx('service-name')}>{service.Name}</span>
                            </CardHeader>
                            <CardDescription>
                                <Flex className={cx('service-extra')}>
                                    <Flex className={cx('service-meta')}>
                                        <Icon name='clock outline' />
                                        <span>
                                            {formatMinutesToWords(
                                                parseInt(service.SuggestedDurationMinutes.toString())
                                            )}
                                        </span>
                                    </Flex>
                                    <Flex className={cx('service-meta')}>
                                        <span className={cx('service-price')}>
                                            {service.SuggestedPrice}
                                            {currencySign ? ` ${currencySign}` : ''}
                                        </span>
                                    </Flex>
                                </Flex>

                                {service.Description ? (
                                    <div className={cx('service-description')}>{service.Description}</div>
                                ) : null}
                            </CardDescription>
                        </CardContent>
                    </Card>
                );
            })}
        </div>

    )
}

interface SimilarServicesSidebarProps extends Props {
    heading?: React.ReactNode
    description?: React.ReactNode
}

// TODO: make sidebar a portal

const SIDEBAR_OPEN_KEY = 'sidebarState';
const SimilarServicesSidebar: React.FC<SimilarServicesSidebarProps> = ({ onSelect = () => { }, heading, description, ...rest }) => {
    const [sidebarOpen, setSidebarOpen] = useState(!!JSON.parse(localStorage.getItem(SIDEBAR_OPEN_KEY) || "false"))
    const services = useAppSelector(s => s.services);

    const { translate } = useTranslate();

    const portalTarget = typeof document !== 'undefined' ? document.body : null;

    const sidebarServiceIds = rest.serviceIds ?? services.data?.map((s: Service) => s.Id);

    useEffect(() => {
        localStorage.setItem(SIDEBAR_OPEN_KEY, JSON.stringify(sidebarOpen))
    }, [sidebarOpen])

    return (
        <>
            {portalTarget
                ? createPortal(
                    <div className={cx('open-wrapper')}>
                        <Button
                            className={cx(['open', sidebarOpen ? 'pushed' : ''])}
                            type="button"
                            content={translate('similarServices.examples')}
                            onClick={() => setSidebarOpen(!sidebarOpen)}
                        />
                    </div>,
                    portalTarget
                )
                : null}

            {portalTarget
                ? createPortal(
                    <Flex className={cx('page-container')} style={rest.style}>
                        <Flex className={cx('page-background')}>
                            <Segment className={cx(['pushable', sidebarOpen ? 'pushed' : ''])}>
                                {(heading || description) && (
                                    <div className={cx('sidebar-header')}>
                                        {heading}
                                        {description}
                                    </div>
                                )}

                                <SimilarServices
                                    {...rest}
                                    enabled={sidebarOpen}
                                    serviceIds={sidebarServiceIds}
                                    onSelect={onSelect}
                                />
                            </Segment>
                        </Flex>
                    </Flex>,
                    portalTarget
                )
                : null}
        </>
    )
}

export { SimilarServicesSidebar, SimilarServices };
